import React, { useState } from 'react';
import { BodyText, FlexColumn, FlexRow, SvgIcon } from '@make-software/cspr-design';
import styled from 'styled-components';
import { Icon } from '@iconify/react';
import { Contact, ContactType, ContactAccessLevel } from 'app/console-api/apps/apps';
import GlobeIcon from 'assets/icons/ic-globe.svg';
import CopyIcon from 'assets/icons/ic-copy.svg';

const ContactItem = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    padding: '8px 12px',
    backgroundColor: theme.styleguideColors.backgroundPrimary,
    borderRadius: '8px',
    gap: '8px',
    alignItems: 'center',
    marginBottom: '8px'
  })
);

const ContactLink = styled.a(({ theme }) =>
  theme.withMedia({
    color: theme.styleguideColors.contentBlue,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  })
);

const CopyButton = styled(SvgIcon)(({ theme }) =>
  theme.withMedia({
    cursor: 'pointer',
    color: theme.styleguideColors.contentSecondary,
    '&:hover': {
      color: theme.styleguideColors.contentBlue
    }
  })
);

const ContactBadge = styled.span(({ theme }) =>
  theme.withMedia({
    padding: '2px 8px',
    backgroundColor: theme.styleguideColors.backgroundSecondary,
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 500,
    color: theme.styleguideColors.contentSecondary,
    marginLeft: 'auto'
  })
);

const ContactTypeIcon = ({ type }: { type: ContactType }) => {
  switch (type) {
    case ContactType.Email:
      return <Icon icon="mdi:email-outline" width={16} height={16} />;
    case ContactType.Phone:
      return <Icon icon="mdi:phone-outline" width={16} height={16} />;
    case ContactType.Link:
      return <SvgIcon src={GlobeIcon} size={16} />;
  }
};

const getContactHref = (type: ContactType, value: string) => {
  switch (type) {
    case ContactType.Phone:
      return `tel:${value}`;
    case ContactType.Link:
      return value.startsWith('http') ? value : `https://${value}`;
    default:
      return value;
  }
};

interface AppContactsViewProps {
  contacts: Contact[];
  showAccessBadge?: boolean;
  emptyMessage?: string;
}

export const AppContactsView = ({
  contacts,
  showAccessBadge = false,
  emptyMessage
}: AppContactsViewProps) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleCopy = (value: string, index: number) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1000);
    });
  };

  if (!contacts || contacts.length === 0) {
    if (!emptyMessage) return null;
    return (
      <BodyText size={3} variation={'darkGray'}>
        {emptyMessage}
      </BodyText>
    );
  }

  return (
    <FlexColumn align="start">
      {contacts.map((contact, index) => (
        <ContactItem key={index}>
          <ContactTypeIcon type={contact.type} />
          <ContactLink
            href={getContactHref(contact.type, contact.value)}
            target="_blank"
            rel="noopener noreferrer">
            {contact.value}
          </ContactLink>
          {showAccessBadge && (
            <ContactBadge>
              {contact.access === ContactAccessLevel.Public ? 'Public' : 'Partners Only'}
            </ContactBadge>
          )}
          <CopyButton src={CopyIcon} size={14} onClick={() => handleCopy(contact.value, index)} />
          {copiedIndex === index && <span style={{ fontSize: 12, color: '#4caf50' }}>Copied!</span>}
        </ContactItem>
      ))}
    </FlexColumn>
  );
};
